import {useEffect, useState} from "react";

import type {NavigationAuthState, NavigationUser} from "./types";

const FIREBASE_CONFIG = {
    projectId: "graviton-mvp",
    appId: "1:706308333141:web:9c78f0286da170472b3edc",
    apiKey: "AIzaSyAXNgauDwq62Ev-SVuub9VrKrGn2eCE6KE",
    authDomain: "auth.flux.ai",
    databaseURL: "https://graviton-mvp.firebaseio.com",
    storageBucket: "graviton-mvp.appspot.com",
    messagingSenderId: "706308333141",
    measurementId: "G-SZ17792FZ5",
} as const;

const FLUX_USER_HANDLE_COOKIE = "flux_user_handle";

function getUserHandleFromCookie(): string | null {
    if (typeof document === "undefined") return null;
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${FLUX_USER_HANDLE_COOKIE}=([^;]+)`));
    const value = match?.[1]?.trim() ?? null;
    return value && value.length > 0 ? decodeURIComponent(value) : null;
}

// See README.md for when to use this vs. a FluxServices adapter.
export function useNavigationAuth(): NavigationAuthState {
    const [user, setUser] = useState<NavigationUser | null>(null);
    const [loading, setLoading] = useState(false);
    const [resolved, setResolved] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") {
            setResolved(true);
            return;
        }

        setLoading(true);

        let unsubscribe: (() => void) | undefined;
        let cancelled = false;

        async function initFirebaseAuth() {
            try {
                const [firebaseApp, firebaseAuth] = await Promise.all([
                    import("firebase/app"),
                    import("firebase/auth"),
                ]);

                if (cancelled) return;

                const existingApps = firebaseApp.getApps();
                const app =
                    existingApps.length > 0
                        ? existingApps[0]!
                        : firebaseApp.initializeApp(FIREBASE_CONFIG, FIREBASE_CONFIG.projectId);

                const auth = firebaseAuth.getAuth(app);

                unsubscribe = firebaseAuth.onAuthStateChanged(
                    auth,
                    (firebaseUser) => {
                        if (cancelled) return;
                        if (firebaseUser && !firebaseUser.isAnonymous) {
                            setUser({
                                uid: firebaseUser.uid,
                                name: firebaseUser.displayName ?? firebaseUser.email ?? "",
                                email: firebaseUser.email ?? "",
                                avatarUrl: firebaseUser.photoURL,
                                handle: getUserHandleFromCookie(),
                            });
                        } else {
                            setUser(null);
                        }
                        setLoading(false);
                        setResolved(true);
                    },
                    () => {
                        if (cancelled) return;
                        setUser(null);
                        setLoading(false);
                        setResolved(true);
                    },
                );
            } catch {
                if (cancelled) return;
                setLoading(false);
                setResolved(true);
            }
        }

        void initFirebaseAuth();

        return () => {
            cancelled = true;
            unsubscribe?.();
        };
    }, []);

    return {user, loading, resolved};
}
